import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Button, Box, Container, ToggleButtonGroup, ToggleButton, CircularProgress, Tooltip, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import ProjectList from '../components/projects/ProjectList';
import ProjectGrid from '../components/projects/ProjectGrid';
import { Project, ProjectCreate, ProjectWithSlideCount } from '../types';
import ProjectFormDialog from '../components/projects/ProjectFormDialog';
import { useAppContext } from '../contexts/AppContext';
import { useLang } from '../contexts/langContext';
import EmptyState from '../components/common/EmptyState';
import { FolderOpen, InfoOutlined, SlideshowOutlined } from '@mui/icons-material';
import { useProjects } from '../hooks/useProjects';
import { getPptxFileCount } from '../services/pptxFiles';
import TagFilterChips from '../components/common/TagFilterChips';
import { useTags } from '../hooks/useTags';
import { useSearchParams } from 'react-router-dom';
import CopilotLauncher from '../components/copilot/CopilotLauncher';
import CopilotPanel from '../components/copilot/CopilotPanel';
import { useState as useReactState } from 'react';

function ProjectsPage() {
  const { lang } = useLang();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [copilotOpen, setCopilotOpen] = useReactState(false);

  // --- Tag selection via URL ---
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTagsParam = searchParams.get('tags');
  const initialSelectedTagIds = initialTagsParam ? initialTagsParam.split(',').filter(Boolean) : [];
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(initialSelectedTagIds);

  const { setAppTitle } = useAppContext();
  const { projects, loading, error: projectsError, handleCreateProject, handleDeleteProject, handleUpdateProjectLocal } = useProjects();
  const { tags } = useTags();
  const [createError, setCreateError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pptxCount, setPptxCount] = useState<number | null>(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [tagCounts, setTagCounts] = useState<Record<string | 'all', number> | undefined>(undefined);

  // Sync selected tags to URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (selectedTagIds.length === 0) {
      params.delete('tags');
    } else {
      params.set('tags', selectedTagIds.join(','));
    }
    setSearchParams(params, { replace: true });
  }, [selectedTagIds]);

  // Filter projects client-side
  const filteredProjects = useMemo(() => {
    if (selectedTagIds.length === 0) return projects;
    return projects.filter(p => selectedTagIds.some(tid => p.tagIds?.includes(tid)));
  }, [projects, selectedTagIds]);

  useEffect(() => {
    setAppTitle(lang === 'en' ? 'Projects' : '项目');
  }, [lang, setAppTitle]);

  useEffect(() => {
    const fetchPptxCount = async () => {
      setLoadingCount(true);
      try {
        const count = await getPptxFileCount();
        setPptxCount(count);
      } catch (error) {
        console.error('Failed to fetch PPTX file count:', error);
      } finally {
        setLoadingCount(false);
      }
    };

    fetchPptxCount();
  }, []);

  // Calculate tag counts whenever projects change
  useEffect(() => {
    if (projects.length > 0) {
      const counts: Record<string | 'all', number> = { all: projects.length };
      projects.forEach(project => {
        (project.tagIds || []).forEach(tagId => {
          counts[tagId] = (counts[tagId] || 0) + 1;
        });
      });
      setTagCounts(counts);
    } else {
      setTagCounts({ all: 0 });
    }
  }, [projects]);

  const handleSubmit = async (projectData: ProjectCreate): Promise<Project> => {
    setCreateError(null);
    setIsSubmitting(true);
    try {
      const project = await handleCreateProject(projectData);
      return project;
    } catch (err: any) {
      if (err.response?.data?.message) {
        setCreateError(err.response.data.message);
      } else {
        setCreateError(lang === 'en' ? 'An error occurred while creating the project' : '创建项目时发生错误');
      }
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogClose = () => {
    setIsCreateDialogOpen(false);
    setCreateError(null);
  };

  const handleProjectUpdated = (id: string, updates: Partial<ProjectWithSlideCount>) => {
    handleUpdateProjectLocal(id, updates);
    
    // Recalculate tag counts when a project is updated
    if (updates.tagIds !== undefined) {
      const updatedProjects = projects.map(project => 
        project._id === id ? { ...project, ...updates } : project
      );
      const counts: Record<string | 'all', number> = { all: updatedProjects.length };
      updatedProjects.forEach(project => {
        (project.tagIds || []).forEach(tagId => {
          counts[tagId] = (counts[tagId] || 0) + 1;
        });
      });
      setTagCounts(counts);
    }
  };

  const noPptxFiles = pptxCount === 0 && !loadingCount;
  const empty = projects.length === 0 && !loading;

  if ((loading || loadingCount) && !isCreateDialogOpen) {
    return (
      <Container sx={{ p: [2, 3], display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (projectsError) {
    return (
      <Container sx={{ p: [2, 3] }}>
        <Typography color="error" align="center">
          {projectsError.message}
        </Typography>
      </Container>
    );
  }

  const createProjectButton = (
    <Button
      variant="contained" 
      onClick={() => setIsCreateDialogOpen(true)} 
      startIcon={<AddIcon />} 
      size={empty ? 'large' : 'medium'}
      sx={empty ? { px: 3, py: 1.5, mt: 2 } : {}}
    >
      {lang === 'en' ? 'Create New Project' : '创建新项目'}
    </Button>
  );

  return (
    <Container sx={{ p: [2, 3] }} maxWidth="md">
      {!empty && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          {createProjectButton}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: empty ? 'center' : 'space-between', alignItems: 'center', mb: 2, mt: empty ? 2 : 0, flexWrap: 'wrap', gap: 1 }}>
        {/* Tag Filter Chips */}
        {!empty && tags.length > 0 && (
          <Box sx={{ }}>
            <TagFilterChips
              tags={tags}
              selectedTagIds={selectedTagIds}
              onChange={setSelectedTagIds}
              tagCounts={tagCounts}
            />
          </Box>
        )}
        {!empty && !noPptxFiles && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newValue) => newValue && setViewMode(newValue)}
            aria-label="view mode"
            size="small"
            sx={{flexGrow: 0, flexShrink: 0}}
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon sx={{ mr: 1 }} />
              {lang === 'en' ? 'List View' : '列表视图'}
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <GridViewIcon sx={{ mr: 1 }} />
              {lang === 'en' ? 'Grid View' : '网格视图'}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>
      {noPptxFiles ? (
        <EmptyState
          imageURL={`/images/slide-show-empty-state.png`}
          message={lang === 'en' ? "No PPTX files found. Please upload PPTX files first to create projects." : "未找到模板文件。请先上传 PPTX 格式的模板文件才能创建项目。"}
          sx={{ py: 0 }}
        />
      ) : empty ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <EmptyState
            imageURL={`/images/slide-show-empty-state.png`}
            message={lang === 'en' ? "No projects yet. Create a new project to get started!" : "还没有项目。开始创建一个新项目吧！"}
            sx={{ py: 0 }}
          />
          {createProjectButton}
          <Alert severity="warning" sx={{ maxWidth: '50%', mt: 3 }} variant="outlined" icon={<InfoOutlined />}>
            {lang === 'en' ? "A project is a collection of draft slides, with materials and images used to build a presentation." : "项目是幻灯片草稿的集合，包括相关材料和图片，用于辅助 AI 构建演示文件。"}
          </Alert>
        </Box>
      ) : filteredProjects.length === 0 ? (
        <EmptyState
          icon={SlideshowOutlined}
          message={lang === 'en' ? "No projects matching the filter." : "没有找到符合条件的项目。"}
          sx={{ py: 0 }}
        />
      ) :
        viewMode === 'list' ? (
        <ProjectList projects={filteredProjects} onDeleteProject={handleDeleteProject} onProjectUpdated={handleProjectUpdated} />
      ) : (
        <ProjectGrid projects={filteredProjects} onDeleteProject={handleDeleteProject} onProjectUpdated={handleProjectUpdated} />
      )}
      {isCreateDialogOpen && (
        <ProjectFormDialog
          open={isCreateDialogOpen}
          onClose={handleDialogClose}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          error={createError}
        />
      )}
      {!copilotOpen && <CopilotLauncher onOpen={() => setCopilotOpen(true)} />}
      <CopilotPanel
        open={copilotOpen}
        onClose={() => setCopilotOpen(false)}
        system={lang === 'en' ? 'You are a helpful copilot for project discovery. Be concise. Prefer using tools to fetch precise project data.' : '你是项目发现助手，简洁回答，优先使用工具获取精确信息。'}
        tools={['project_search', 'project_get', 'material_search', 'material_list']}
      />
    </Container>
  );
}

export default ProjectsPage;
